/** @format */

import {
  BeaconQueryParameters,
  CriteoDigitalShelfTalker,
  CriteoProduct,
  CriteoTrackingItem,
  CriteoViewProductsResponse,
} from '@shared/data-access/interfaces';
import { DisplayableProductListItem, ProductList } from '@plp/data-access/interfaces';
import { ElementRef, Injectable, inject } from '@angular/core';
import { MLItem, MLItemType } from '@machine-learning/data-access';

import { AnalyticsService } from '@shared/data-access/services';
import { CriteoService, DmnAdService } from '@shared/data-access/product-recommendations';
import { ProductListMlService } from './product-list-ml.service';

@Injectable({
  providedIn: 'root',
})
export class ProductListTrackingService {
  private readonly plpMlService = inject(ProductListMlService);
  private readonly criteoService = inject(CriteoService);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly analyticsService = inject(AnalyticsService);
  private currentFacets: {
    facet: string;
    values: {
      count: number;
      value: string;
    }[];
  }[] = [];

  private productInfoList: string[] = [];
  private chainAbbr = '';
  private productCardObserver: IntersectionObserver | null = null;
  private gridViewBeaconFired = false;
  private ecodesAlreadyReported: string[] = [];
  private productListData: ProductList | null = null;
  private onViewGridItemBeacon = '';
  private isUsingCriteoSPAAdServer = false;
  private readonly productCardViewBeacons: string[] = [];

  /** ** Start of ML Integration properties ************************/
  private readonly intersectionParams: IntersectionObserverInit = {
    // This is added to account for the space taken up by the product-list-header.
    // If the user is scrolling up, the impression will wait to fire until
    // the last portion of the image is out from under the header.
    root: null,
    rootMargin: '0%',
    threshold: 1, // 0.0 - 1.0 = 0% - 100%
  };

  resetTheObserver = (): void => {
    this.productCardObserver = new IntersectionObserver(
      this.handleProductCardIntersection,
      this.intersectionParams,
    );
  };

  setupProductCardObservers(
    rootElement: ElementRef,
    dpld: DisplayableProductListItem[],
    productList: ProductList | null,
    currentFacets: {
      facet: string;
      values: { count: number; value: string }[];
    }[],
    chainAbbr: string,
    isUsingCriteoSPAAdServer: boolean,
  ): void {
    // update local state for use in callbacks
    this.currentFacets = currentFacets;
    this.productListData = productList;
    this.chainAbbr = chainAbbr;
    this.isUsingCriteoSPAAdServer = isUsingCriteoSPAAdServer;

    // Clear the list of ecodes that have been reported to the ML API
    this.ecodesAlreadyReported = [];
    this.productCardObserver?.disconnect();
    this.resetTheObserver();

    // This is used to track the ecodes of the products that are displayed on the page.
    // parentPartnumber -> regular products, product_id -> sponsored products
    this.productInfoList = this.generateProductInfoList(dpld);

    dpld.forEach((x, index) => {
      const pcimg: Element | null =
        rootElement.nativeElement.querySelector(
          `#plp-container #product-card-${index} .product-card-image`,
        ) ??
        rootElement.nativeElement.querySelector(
          `#plp-container #sponsored-product-${index} .product-card-image`,
        ) ??
        rootElement.nativeElement.querySelector(
          `#plp-container #pinned-content-${index} .pinned-content-intersection-observation-block`,
        ) ??
        rootElement.nativeElement.querySelector(
          `#plp-container #sponsored-pinned-content-${index} .pinned-content-intersection-observation-block`,
        );

      if (pcimg) {
        this.productCardObserver?.observe(pcimg);
      }
    });
  }

  private readonly handleProductCardIntersection = (entries: IntersectionObserverEntry[]): void => {
    const itemGroup: MLItem[] = [];
    const intersectingCards = entries.filter((entry) => entry.isIntersecting);
    if (!intersectingCards.length) {
      return;
    }

    for (const item of intersectingCards) {
      const card:
        | { index: number; isPinnedProduct: boolean | undefined; type: string }
        | undefined = this.getProductCardMetadata(item.target);

      if (card === undefined) {
        return;
      }

      const type = this.getMLItemType(card);

      if (
        type === MLItemType.SPONSORED_PRODUCT &&
        this.isUsingCriteoSPAAdServer &&
        !this.gridViewBeaconFired
      ) {
        this.dmnAdService.sendBeacon(this.onViewGridItemBeacon, {
          placementType: 'INGRID',
          placementLevel: 'PLACEMENT',
          beaconType: 'VIEW',
        });
        this.gridViewBeaconFired = true;
      }

      const ecode: string = this.productInfoList[card.index];

      if (this.ecodesAlreadyReported.includes(ecode)) {
        continue;
      }
      this.ecodesAlreadyReported.push(ecode);

      const nextItem: MLItem = {
        id: ecode,
        num: card.index + 1,
        type,
      };
      itemGroup.push(nextItem);

      this.productCardObserver?.unobserve(item.target);
    }

    this.plpMlService.sendImpressionEventData(
      itemGroup,
      this.currentFacets,
      this.chainAbbr,
      this.productListData?.facetVOs ?? [],
      this.productListData?.totalCount ?? 0,
    );
  };

  addSponsoredIntersectionObservers(
    criteoProducts: CriteoViewProductsResponse | null,
    criteoTracking: (CriteoTrackingItem | undefined)[],
  ): void {
    const dstPlacement = criteoProducts?.placements.digitalShelfTalker;
    if (dstPlacement) {
      this.setupSponsoredPinnedContentObserver(dstPlacement, criteoTracking);
    }
    const inGridPlacement = criteoProducts?.placements.inGrid?.[0];
    if (inGridPlacement) {
      this.setupInGridSponsoredProductObservers(
        inGridPlacement?.products,
        inGridPlacement?.OnViewBeacon,
        criteoTracking,
      );
    }
  }

  private setupSponsoredPinnedContentObserver(
    dst: CriteoDigitalShelfTalker,
    criteoTracking: (CriteoTrackingItem | undefined)[],
  ): void {
    if (!dst) return;

    const dstElement = document.querySelector('[id^="sponsored-pinned-content-"]');
    if (dstElement instanceof HTMLElement) {
      const trackingItem = criteoTracking[this.getSponsoredElementIndex(dstElement)];
      const dstCallback = () => {
        this.dmnAdService.sendBeacon(dst.OnViewBeacon, {
          placementType: 'DST',
          placementLevel: 'PLACEMENT',
          beaconType: 'VIEW',
        });
        this.analyticsService.triggerAnalyticsEvent('contentImpression', trackingItem);
      };
      this.criteoService.sponsoredProductsIntersectionObserver(dstElement, dstCallback);
    }
  }

  private setupInGridSponsoredProductObservers(
    allProducts: CriteoProduct[],
    inGridPlacementBeacon: string,
    criteoTracking: (CriteoTrackingItem | undefined)[],
  ): void {
    if (!allProducts?.length) return;

    this.onViewGridItemBeacon = inGridPlacementBeacon;

    this.pollForCriteoElements(allProducts, criteoTracking);
    this.gridViewBeaconFired = false;
  }

  private pollForCriteoElements(
    allProducts: CriteoProduct[],
    criteoTracking: (CriteoTrackingItem | undefined)[],
    processedProductIds: Set<string> = new Set<string>(),
    currentTry = 0,
  ) {
    // Poll for the elements to ensure they exist before attaching observers.
    // This prevents race conditions on slower networks/devices.
    const maxRetries = 50; // Poll for a total of 5 seconds (50 * 100ms)
    const unprocessedProducts = allProducts.filter((p) => !processedProductIds.has(p.ProductId));

    for (const item of unprocessedProducts) {
      const trackingId = `criteo-${item.ProductId}`;
      const sponsoredProductCard = document.getElementsByClassName(trackingId)[0] as HTMLElement;

      if (sponsoredProductCard) {
        if (!this.productCardViewBeacons.includes(item.OnViewBeacon)) {
          const trackingItem = criteoTracking[this.getSponsoredElementIndex(sponsoredProductCard)];
          const queryParameters: BeaconQueryParameters = {
            placementType: 'INGRID',
            placementLevel: 'PRODUCT',
            beaconType: 'VIEW',
          };
          const criteoCallback = () => {
            this.dmnAdService.sendBeacon(item.OnViewBeacon, queryParameters);
            this.analyticsService.triggerAnalyticsEvent('contentImpression', trackingItem);
          };
          this.criteoService.sponsoredProductsIntersectionObserver(
            sponsoredProductCard,
            criteoCallback,
          );
          this.productCardViewBeacons.push(item.OnViewBeacon);
        }
        processedProductIds.add(item.ProductId);
      }
    }

    // If there are still unprocessed products and we haven't reached the max retries, poll again.
    if (processedProductIds.size < allProducts.length && currentTry < maxRetries) {
      setTimeout(
        () =>
          this.pollForCriteoElements(
            allProducts,
            criteoTracking,
            processedProductIds,
            currentTry + 1,
          ),
        100,
      );
    }
  }

  private getSponsoredElementIndex(element: HTMLElement): number {
    const index = /(?:sponsored-product|sponsored-pinned-content)-(\d+)$/.exec(element.id)?.[1];
    return Number(index);
  }

  generateProductInfoList(dpld: DisplayableProductListItem[]): string[] {
    return dpld.map((item: DisplayableProductListItem): string => {
      if ('parentPartnumber' in item && item.parentPartnumber) {
        return item.parentPartnumber;
      } else if ('product_id' in item && item.product_id) {
        return item.product_id;
      } else if ('csid' in item && item.csid) {
        return 'onViewBeacon' in item && item.onViewBeacon
          ? item.csid
          : `${item.csid}-${item.pinnedPosition}`;
      } else return '';
    });
  }

  /**
   * Starting from the target element, recursively step up to the next parent element
   * that contains the id number found in `product-card-`, `sponsored-product-`, or `pinned-content-`
   * used to obtain the list of ecodes in each respective product row.
   */
  private getProductCardMetadata(
    el: Element | HTMLElement | null,
  ): { index: number; isPinnedProduct: boolean | undefined; type: string } | undefined {
    const parentCardIdMatch: RegExpMatchArray | null =
      /^(product-card-|sponsored-product-|pinned-content-|sponsored-pinned-content-)\d+$/.exec(
        el?.id ?? '',
      );

    if (parentCardIdMatch && el?.id) {
      // "product-card-5" => {index: 5, isPinnedProduct: false, type: "product-card"}
      const isPinnedProduct: boolean | undefined = (el as HTMLElement).dataset.em?.includes(
        '_pinned',
      );
      const splitProductId = el.id.split('-');
      const index = Number.parseInt(splitProductId.at(-1) ?? '', 10);
      const type = parentCardIdMatch[1].slice(0, -1); // use the regex match term and trim off the '-' at the end => "product-card"
      return { index, isPinnedProduct, type };
    } else if (el?.parentElement) {
      return this.getProductCardMetadata(el.parentElement);
    } else {
      return undefined;
    }
  }

  private getMLItemType(card: {
    index: number;
    isPinnedProduct: boolean | undefined;
    type: string;
  }): MLItemType {
    if (card.isPinnedProduct) {
      return MLItemType.PINNED_PRODUCT;
    } else if (card.type === 'sponsored-product') {
      return MLItemType.SPONSORED_PRODUCT;
    } else if (card.type === 'pinned-content') {
      return MLItemType.PINNED_CONTENT_SPOT;
    } else if (card.type === 'sponsored-pinned-content') {
      return MLItemType.SPONSORED_PINNED_CONTENT;
    }
    return MLItemType.PRODUCT;
  }
}
